import { Info } from "lucide-react";
import { forwardRef } from "react";
import type { SettingsSection } from "@/lib/settings-schema";
import { sectionIcon } from "./icons";
import { SettingRow, type SettingRowView } from "./setting-row";

/**
 * A titled config section: icon chip + heading + blurb, a hairline-divided card
 * of rows, and an optional footnote. `forwardRef` so the section nav can read
 * each section's `offsetTop` for scroll + scroll-spy.
 */
export const SettingSection = forwardRef<
	HTMLElement,
	{ section: SettingsSection; rows: SettingRowView[]; onSaved: () => void }
>(({ section, rows, onSaved }, ref) => {
	const Icon = sectionIcon(section.icon);
	return (
		<section ref={ref} id={`settings-${section.id}`} className="scroll-mt-4">
			<div className="mb-3 flex items-start gap-3">
				<span className="mt-0.5 flex size-8 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-muted">
					<Icon className="size-4" />
				</span>
				<div>
					<h2 className="headline text-foreground">{section.title}</h2>
					{section.blurb ? (
						<p className="mt-0.5 max-w-2xl text-body-sm text-ink-subtle">{section.blurb}</p>
					) : null}
				</div>
			</div>
			<div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
				{rows.map((row, i) => (
					<SettingRow key={row.key} row={row} last={i === rows.length - 1} onSaved={onSaved} />
				))}
			</div>
			{section.footnote ? (
				<div className="mt-2 flex items-start gap-2 px-1 text-meta text-ink-subtle">
					<Info className="mt-px size-3.5 flex-none text-ink-tertiary" />
					<p className="max-w-2xl text-pretty leading-snug">{section.footnote}</p>
				</div>
			) : null}
		</section>
	);
});

SettingSection.displayName = "SettingSection";
